import * as React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Clock, FileText, Folder as FolderIcon, GitBranch } from "lucide-react";
import { create } from "zustand";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  filterRecentFilesByExisting,
  formatExactDateTime,
  formatFriendlyTime,
  parseFilePathDisplay,
} from "@/features/project/lib/recentFilesHelpers";
import { api } from "@/lib/convex-api";
import { projectRoute } from "@/lib/app-routes";
import {
  getProjectRemoteLabel,
  getProjectSourceLabel,
  isLocalProject,
} from "@/features/project-source/source-helpers";
import { useProjectUIStore } from "@/features/project/stores/project-ui-store";
import { useIsDesktop } from "@/hooks/use-media-query";
import type { Id } from "../../../backend/convex/_generated/dataModel";

// ---------------------------------------------------------------------------
// Zustand store — lets any component open/close the palette
// ---------------------------------------------------------------------------

interface CommandPaletteState {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const useCommandPaletteStore = create<CommandPaletteState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/** Convenience — open from anywhere without a hook */
export const openCommandPalette = () =>
  useCommandPaletteStore.setState({ open: true });

// ---------------------------------------------------------------------------
// Keyboard shortcut (Cmd/Ctrl + K)
// ---------------------------------------------------------------------------

export function useCommandPaletteShortcut() {
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const navigate = useNavigate();
  const unsafeNavigate = navigate as unknown as (args: any) => Promise<void>;
  const isDesktop = useIsDesktop();
  const cmdkInUrl = useRouterState({
    select: (s) =>
      (s.location.search as unknown as { cmdk?: "1" } | undefined)?.cmdk ===
      "1",
  });

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isDesktop) {
          useCommandPaletteStore.setState((s) => ({ open: !s.open }));
          return;
        }

        // On mobile, drive open/close via router history so "Back" closes.
        if (cmdkInUrl) {
          void unsafeNavigate({
            search: (prev: Record<string, unknown>) => ({
              ...prev,
              cmdk: undefined,
            }),
            replace: true,
          });
        } else {
          void unsafeNavigate({
            search: (prev: Record<string, unknown>) => ({
              ...prev,
              cmdk: "1",
            }),
          });
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [cmdkInUrl, isDesktop, unsafeNavigate, setOpen]);
}

// ---------------------------------------------------------------------------
// CommandPalette component
// ---------------------------------------------------------------------------

export function CommandPalette() {
  const { open, setOpen } = useCommandPaletteStore();
  const navigate = useNavigate();
  const unsafeNavigate = navigate as unknown as (args: any) => Promise<void>;
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const [search, setSearch] = React.useState("");
  const cmdkInUrl = useRouterState({
    select: (s) =>
      (s.location.search as unknown as { cmdk?: "1" } | undefined)?.cmdk ===
      "1",
  });
  const prevCmdkInUrlRef = React.useRef(cmdkInUrl);

  // Fetch all user projects
  const projects = useQuery(api.projects.listForUser, {});

  // Detect if on a project page and extract project ID
  const pathname = useRouterState({
    select: (s) => s.location.pathname,
  });
  const projectMatch = pathname.match(
    /^\/([^/]+)\/(?!new-project(?:\/|$))([^/]+)(?:\/|$)/,
  );
  const currentOrganizationSlug = projectMatch?.[1] ?? null;
  const currentProjectSlug = projectMatch?.[2] ?? null;

  // Find the current project name from the projects list
  const currentProject = React.useMemo(
    () =>
      projects?.find(
        (p) =>
          p.organizationSlug === currentOrganizationSlug &&
          p.slug === currentProjectSlug,
      ),
    [currentOrganizationSlug, currentProjectSlug, projects],
  );
  const currentProjectId = currentProject?.id ?? null;

  // Fetch files for current project (skip when not on a project page)
  const files = useQuery(
    api.projectFiles.list,
    currentProjectId
      ? { projectId: currentProjectId as Id<"projects"> }
      : "skip",
  ) as Array<{ path: string }> | undefined;

  const selectFile = useProjectUIStore((s) => s.selectFile);
  const recentFiles = useProjectUIStore((s) => s.recentFiles);

  // Reset search when dialog closes
  React.useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const closePalette = React.useCallback(() => {
    setOpen(false);
    if (!isMobile) return;
    void unsafeNavigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        cmdk: undefined,
      }),
      replace: true,
    });
  }, [isMobile, unsafeNavigate, setOpen]);

  // Mobile: close the palette when the user hits browser Back.
  React.useEffect(() => {
    if (!isMobile) return;
    const prev = prevCmdkInUrlRef.current;
    prevCmdkInUrlRef.current = cmdkInUrl;
    if (prev && !cmdkInUrl && open) {
      setOpen(false);
    }
  }, [cmdkInUrl, isMobile, open, setOpen]);

  // Mobile: if the URL indicates cmdk is open, ensure the dialog is open.
  React.useEffect(() => {
    if (!isMobile) return;
    if (cmdkInUrl && !open) setOpen(true);
  }, [cmdkInUrl, isMobile, open, setOpen]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!isMobile) return;

      if (nextOpen) {
        void unsafeNavigate({
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            cmdk: "1",
          }),
        });
      } else {
        void unsafeNavigate({
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            cmdk: undefined,
          }),
          replace: true,
        });
      }
    },
    [isMobile, unsafeNavigate, setOpen],
  );

  const query = search.trim().toLowerCase();
  const isSearching = query.length > 0;

  // Recent files — shown when on a project page with no search query
  // Filter out any paths that no longer exist in the current file list
  const recentFileItems = React.useMemo(() => {
    if (!currentProjectId || isSearching || recentFiles.length === 0) return [];
    if (!files) return [];
    const existingPaths = new Set<string>(
      files.map((f: { path: string }) => f.path),
    );
    return filterRecentFilesByExisting(recentFiles, existingPaths);
  }, [currentProjectId, isSearching, recentFiles, files]);

  // Filter projects
  const filteredProjects = React.useMemo(() => {
    if (!projects) return [];
    if (!isSearching) return projects.slice(0, 8);
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        getProjectSourceLabel(p).toLowerCase().includes(query) ||
        (getProjectRemoteLabel(p) ?? "").toLowerCase().includes(query),
    );
  }, [projects, query, isSearching]);

  // Filter files — flat search across all file paths (only when typing)
  const filteredFiles = React.useMemo(() => {
    if (!files || !currentProjectId || !isSearching) return [];
    return files
      .filter((f: { path: string }) => {
        const lowerPath = f.path.toLowerCase();
        const fileName = f.path.split("/").pop()?.toLowerCase() ?? "";
        return lowerPath.includes(query) || fileName.includes(query);
      })
      .slice(0, 20);
  }, [files, query, currentProjectId, isSearching]);

  const handleSelectProject = React.useCallback(
    (project: { organizationSlug?: string; slug?: string }) => {
      if (!project.organizationSlug || !project.slug) {
        return;
      }
      unsafeNavigate({
        to: projectRoute as string,
        params: { orgSlug: project.organizationSlug, id: project.slug },
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          cmdk: undefined,
        }),
      });
      closePalette();
    },
    [closePalette, unsafeNavigate],
  );

  const handleSelectFile = React.useCallback(
    (filePath: string) => {
      selectFile(filePath);
      closePalette();
    },
    [closePalette, selectFile],
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Search"
      description="Search files and projects"
    >
      <CommandInput
        placeholder={
          currentProjectId ? "Search files and projects…" : "Search projects…"
        }
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>No results found</CommandEmpty>

        {/* Recent files — shown by default (no query) on a project page */}
        {recentFileItems.length > 0 && (
          <CommandGroup heading="Recent Files">
            {recentFileItems.map((entry) => {
              const { fileName, dirPath } = parseFilePathDisplay(entry.path);
              return (
                <CommandItem
                  key={entry.path}
                  value={`recent:${entry.path}`}
                  onSelect={() => handleSelectFile(entry.path)}
                >
                  <Clock className="shrink-0 text-muted-foreground" />
                  <span className="truncate">{fileName}</span>
                  {dirPath && (
                    <span className="truncate text-xs text-muted-foreground/50">
                      {dirPath}
                    </span>
                  )}
                  <span
                    className="ml-auto shrink-0 text-xs text-muted-foreground/50"
                    title={formatExactDateTime(entry.lastAccessedAt)}
                  >
                    {formatFriendlyTime(entry.lastAccessedAt)}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Files — search results, only shown when on a project page and typing */}
        {currentProjectId && filteredFiles.length > 0 && (
          <CommandGroup
            heading={
              currentProject ? `Files in ${currentProject.name}` : "Files"
            }
          >
            {filteredFiles.map((file: { path: string }) => {
              const { fileName, dirPath } = parseFilePathDisplay(file.path);
              return (
                <CommandItem
                  key={file.path}
                  value={`file:${file.path}`}
                  onSelect={() => handleSelectFile(file.path)}
                >
                  <FileText className="shrink-0 text-muted-foreground" />
                  <span className="truncate">{fileName}</span>
                  {dirPath && (
                    <span className="ml-auto max-w-[40%] truncate text-xs text-muted-foreground/50">
                      {dirPath}
                    </span>
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Projects */}
        {filteredProjects.length > 0 && (
          <CommandGroup heading="Projects">
            {filteredProjects.map((project) => (
              <CommandItem
                key={project.id}
                value={`project:${project.id}:${project.name}`}
                onSelect={() => handleSelectProject(project)}
              >
                <FolderIcon className="shrink-0 text-muted-foreground" />
                <span className="truncate">{project.name}</span>
                <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground/50">
                  <GitBranch className="size-3 shrink-0" />
                  <span className="truncate">
                    {isLocalProject(project)
                      ? getProjectSourceLabel(project)
                      : (getProjectRemoteLabel(project) ??
                        "Remote unavailable")}
                  </span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
